import { useState, useEffect, useCallback } from 'react'
import { get, put } from '../lib/api'

export interface AiSettings {
  aiProvider: string
  aiModel: string
  aiBaseUrl: string
  aiKeyConfigured: boolean
}

export interface CrawlerSettings {
  crawlWanted: boolean
  crawlLinkedin: boolean
  crawlJobkorea: boolean
}

function useSettingsLoader<T>(path: string) {
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await get<T>(path)
      setData(result)
    } catch (e) {
      setError(e instanceof Error ? e.message : '알 수 없는 오류')
    } finally {
      setLoading(false)
    }
  }, [path])

  useEffect(() => {
    load()
  }, [load])

  const save = useCallback(
    async (body: unknown) => {
      setSaving(true)
      setError(null)
      setSaved(false)
      try {
        const result = await put<T>(path, body)
        setData(result)
        setSaved(true)
        setTimeout(() => setSaved(false), 2000)
      } catch (e) {
        setError(e instanceof Error ? e.message : '저장 실패')
      } finally {
        setSaving(false)
      }
    },
    [path],
  )

  return { data, loading, error, saving, saved, reload: load, save }
}

export function useAiSettings() {
  return useSettingsLoader<AiSettings>('/api/v1/settings/ai')
}

export function useCrawlers() {
  return useSettingsLoader<CrawlerSettings>('/api/v1/settings/crawlers')
}
